import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { CardViewStyles, TOTAL_CARD_HEIGHT, TOTAL_CARD_WIDTH } from './CardViewStyles';
import { AppState } from '../../../app/state/AppState';
import { ContentTypeFilterValue } from '../../models/ContentTypeFilterValue';
import { getKeyForItem, SearchResultItem, SearchResultList } from '../../models/SearchResultTypes';
import { ContentType } from '../../models/ContentType';
import { ConnectedContentCardWrapper } from './ContentCardWrapper';
import { requestContentSlice } from '../../actions/RequestContentSliceAction';
import { CardScroller } from './CardScroller';
import { PageType } from '../../models/PageType';
import { SearchStatus } from '../SearchResultPage';

export const SpaceBetweenCardsInPx = 18;

export const CardContainerHorizontalPadding = 0;

export interface CardViewOwnProps {
  readonly contentType: ContentType;
  readonly items: SearchResultItem[];
  readonly pageType: PageType;
}

export const CardLayoutTestID = 'CardLayout';
export const CardScrollerTestID = 'CardScroller';

export type CardViewProps = CardViewOwnProps & CardViewStateProps & CardViewDispatchProps;

export class CardView extends React.Component<CardViewProps> {
  public constructor(props: CardViewProps) {
    super(props);
  }

  public render() {
    if (!this.props.isSearchComplete) {
      // If the initial render (which doesn't call shouldComponentUpdate) is still busy fetching items
      // Don't render anything.
      return null;
    }
    if (this.props.pageType === PageType.mixed) {
      const cards = this.props.items.map((item: SearchResultItem, id: number) => {
        return (
          <div className={CardViewStyles.SectionCardDimensions} key={id}>
            <ConnectedContentCardWrapper
              content={item}
              itemIndex={id}
              contentType={this.props.contentType}
              totalCount={this.props.items.length}
            />
          </div>
        );
      });
      return (
        <div data-tb-test-id={CardLayoutTestID} className={[CardViewStyles.Section, CardViewStyles.CardContainer].join(' ')}>
          {cards}
        </div>
      );
    } else {
      const items = this.props.searchResults?.list;
      const itemsTotal = this.props.searchResults ? this.props.searchResults.totalCount : 0;
      const scroller = (
        <div data-tb-test-id={CardScrollerTestID} className={CardViewStyles.CardContainer}>
          <CardScroller
            items={items}
            itemsTotal={itemsTotal}
            requestContentSlice={this.props.requestContentSlice}
            renderFetchedCard={this.renderCard}
            renderUnfetchedCard={UnfetchedCard}
            getKeyForItem={getKeyForItem}
            itemHeightInPx={TOTAL_CARD_HEIGHT}
            itemWidthInPx={TOTAL_CARD_WIDTH}
            itemSpacingInPx={SpaceBetweenCardsInPx}
            sectionHorizontalPaddingInPx={CardContainerHorizontalPadding}
            contentType={this.props.contentType}
          />
        </div>
      );
      return scroller;
    }
  }

  private renderCard = (cardContent: { item: SearchResultItem; index: number; row: number }): JSX.Element => {
    const { item, index } = cardContent;
    return (
      <ConnectedContentCardWrapper
        content={item}
        itemIndex={index}
        contentType={this.props.contentType}
        row={cardContent.row}
        totalCount={this.props.items.length}
      />
    );
  };
}

function UnfetchedCard() {
  return (
    <div className={`tb-unfetched-card ${CardViewStyles.Card}`}>
      <div className={CardViewStyles.Thumbnail} />
    </div>
  );
}

export interface CardViewStateProps {
  readonly isSearchComplete: boolean;
  readonly searchResults: SearchResultList<SearchResultItem> | undefined;
  readonly selectedContentTypeFilterValue: ContentTypeFilterValue;
}

export interface CardViewDispatchProps {
  readonly requestContentSlice: (offset: number, count: number, contentType: ContentType) => void;
}

const mapStateToProps = (state: AppState) => {
  return {
    searchResults: state.singleContentType?.searchResults,
    selectedContentTypeFilterValue: state.search.selectedContentTypeFilterValue,
    isSearchComplete: state.search.status === SearchStatus.searchComplete,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      requestContentSlice,
    },
    dispatch
  );

export const ConnectedCardView = connect(mapStateToProps, mapDispatchToProps)(CardView);
